import React, { useEffect, useState } from 'react';

import { Spinner } from 'components/Spinner/spinner';
import ResizableColumns from 'components/ResizableColumns/ResizableColumns';
import ResizableRows from 'components/ResizableRows/ResizableRows';
import FreecodeTabSelector from 'components/FreecodeTabSelector/FreecodeTabSelector';
import { useFreecodeContext } from 'hooks/useContext';
import { identifiers, tabLabels } from './FreecodeLayoutConstants';
import { LanguageSelection } from 'components/FreecodeContent/FreecodeButtons';

const descriptionContainerOptions = [identifiers.description, identifiers.summary, identifiers.course, identifiers.api];

const solutionContainerOptions = [
  identifiers.implementation,
  identifiers.solution,
  identifiers.discussion,
  identifiers.apiSettings,
  identifiers.graphicsSettings,
  identifiers.settings,
  identifiers.preview,
];

const rightColumnOptions = [identifiers.canvas, identifiers.console];

/**
 * Given a map with component display them in three columns
 */
export default function FreecodeLayoutWide({ componentMap }) {
  const [leftColumnItems, setLeftColumnItems] = useState([]);
  const [middleColumnItems, setMiddleColumnItems] = useState([]);
  const [rightColumnItems, setRightColumnItems] = useState([]);
  const [canvasPercentage, setCanvasPercentage] = useState(25);

  const { testImpl } = useFreecodeContext();

  useEffect(() => {
    let newLeftColumn = [];
    for (const keyName of descriptionContainerOptions) {
      if (componentMap[keyName]) newLeftColumn.push(keyName);
    }

    let newMiddleColumn = [];
    for (const keyName of solutionContainerOptions) {
      if (componentMap[keyName]) newMiddleColumn.push(keyName);
    }

    let newRightColumn = [];
    for (const keyName of rightColumnOptions) {
      if (componentMap[keyName]) newRightColumn.push(keyName);
    }

    setLeftColumnItems(newLeftColumn);
    setMiddleColumnItems(newMiddleColumn);
    setRightColumnItems(newRightColumn);
  }, [componentMap]);

  useEffect(() => {
    if (!rightColumnItems.includes(identifiers.canvas)) return;
    // Offset header height
    let columnHeight = window.innerHeight - 55;
    let columnWidth = window.innerWidth / 3;

    let widthUnit = columnWidth / testImpl.configCanvas.width;
    // offset canvas header, footer, and draggable gutter size
    let desiredCanvasHeight = widthUnit * testImpl.configCanvas.height + 78;

    let desiredHeightPercentage = (100 * desiredCanvasHeight) / columnHeight;

    setCanvasPercentage(Math.min(desiredHeightPercentage, 75));
    window.dispatchEvent(new Event('resize'));
  }, [testImpl.configCanvas, rightColumnItems]);

  const renderLeftColumn = () => {
    if (!leftColumnItems.length) return <Spinner key="leftSpinner" />;
    return (
      <FreecodeTabSelector
        items={leftColumnItems.map((KeyName) => ({
          label: tabLabels[KeyName]?.label,
          icon: tabLabels[KeyName]?.icon,
          tabId: tabLabels[KeyName]?.tabId,
          component: componentMap[KeyName],
        }))}
      />
    );
  };

  const renderMiddleColumn = () => {
    if (!middleColumnItems.length) return <Spinner key="rightSpinner" />;
    return (
      <FreecodeTabSelector
        items={middleColumnItems.map((KeyName) => ({
          label: tabLabels[KeyName]?.label,
          icon: tabLabels[KeyName]?.icon,
          tabId: tabLabels[KeyName]?.tabId,
          component: componentMap[KeyName],
        }))}
        headerComponents={<LanguageSelection />}
      />
    );
  };

  const renderRightColumn = () => {
    if (!rightColumnItems.length) return <Spinner key="rightSpinner" />;
    if (rightColumnItems.length === 1) return componentMap[rightColumnItems[0]];
    return (
      <ResizableRows key="RightColumn" sizes={[canvasPercentage, 100 - canvasPercentage]}>
        {componentMap[rightColumnItems[0]]}
        {componentMap[rightColumnItems[1]]}
      </ResizableRows>
    );
  };

  return (
    <ResizableColumns key="wideScreenLayout">
      {renderLeftColumn()}
      {renderMiddleColumn()}
      {renderRightColumn()}
    </ResizableColumns>
  );
}
